// Central, user-friendly classification of API errors, so the user always knows
// WHAT happened: a network/connection problem, a server fault, an authentication
// or permission issue, a not-found, or invalid input — rather than a generic or
// misleading message.

// A response body that is a bare string is only a usable message if it LOOKS
// like one. Proxies, gateways and ngrok answer failures with a full HTML error
// page, and rendering that verbatim is worse than saying nothing: it buries the
// screen in markup and hides the real classification describeError() would have
// given. Anything document-shaped or essay-length is rejected.
const MAX_SERVER_MESSAGE = 300;

function usableMessage(value) {
  if (typeof value !== 'string') return '';
  const s = value.trim();
  if (!s) return '';
  if (s.length > MAX_SERVER_MESSAGE) return '';        // a document, not a sentence
  if (/^</.test(s) || /<!doctype|<html/i.test(s)) return '';
  return s;
}

// Read a server-supplied message out of a response body, tolerating the shapes
// this API uses (sometimes nested under `data`, varying key casing). Returns ''
// when the body carries no message worth showing — callers then fall back to
// describeError(), which always produces something meaningful.
export function readServerMessage(payload) {
  const root = (payload && typeof payload === 'object'
                && payload.data && typeof payload.data === 'object')
    ? payload.data
    : payload;
  if (root == null) return '';
  if (typeof root === 'string') return usableMessage(root);
  if (typeof root !== 'object') return '';
  return usableMessage(root.message || root.msg || root.error_message || root.errorMessage || '');
}

// Map an HTTP status code (+ optional server message) to a clear sentence.
export function messageForStatus(status, serverMessage) {
  const msg = serverMessage && String(serverMessage).trim();

  if (status === 400 || status === 422) {
    // Bad/invalid input — prefer the server's specific validation text.
    return msg || 'Some of the details you entered are invalid. Please review and try again.';
  }
  if (status === 401) {
    return msg || 'Authentication failed. Please check your credentials or sign in again.';
  }
  if (status === 403) {
    return msg || 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return msg || 'Not found: the requested item or endpoint does not exist on the server. It may have been removed, or this feature/API is not available.';
  }
  if (status === 409) {
    return msg || 'This conflicts with existing data (it may already exist).';
  }
  if (status === 501) {
    return 'This feature is not implemented on the server yet.';
  }
  if (status === 503) {
    return 'The service is temporarily unavailable. Please try again in a little while.';
  }
  if (status >= 500) {
    // Don't surface raw server internals on 5xx — keep it safe and generic.
    return 'The server ran into a problem. Please try again in a little while.';
  }
  return msg || `The request failed (error ${status}). Please try again.`;
}

// Pull the request path (no host) out of an axios error, for diagnostics.
function getRequestPath(err) {
  const url = (err && err.config && err.config.url) || (err && err.request && err.request.responseURL);
  if (!url) return '';
  try {
    return new URL(url, window.location.origin).pathname;
  } catch (e) {
    return url;
  }
}

// Classify an axios-style error object into a clear message.
export function describeError(err) {
  // 1) Server responded with a non-2xx status → categorize by status code.
  if (err && err.response) {
    // Guarded read: on a 400/404 messageForStatus prefers the server's text, so
    // an HTML body must never reach it.
    const serverMessage = readServerMessage(err.response.data);
    const base = messageForStatus(err.response.status, serverMessage);
    // For 404 include the endpoint that was missing, so it's clear WHAT was not found.
    if (err.response.status === 404) {
      const path = getRequestPath(err);
      return path ? `${base} (${path})` : base;
    }
    return base;
  }
  // 2) Request was sent but no response arrived → server unreachable / network /
  //    CORS / timeout. (axios sets err.request and err.message === 'Network Error'.)
  if (err && err.request) {
    return 'Cannot reach the server. Please check your internet connection and try again.';
  }
  // 3) Something failed before the request was sent (e.g. a client-side bug).
  return (err && err.message) || 'An unexpected error occurred. Please try again.';
}
